import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { addYears } from 'date-fns';
import { CookieService } from 'ngx-cookie-service';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Practice } from 'src/app/models/Practice';
import { User } from 'src/app/models/User';
import { Noop } from 'src/app/state/actions';
import { AppState } from 'src/app/state/reducers';
import { UserService } from 'src/app/users/user.service';
import * as PracticeActions from './actions';
import { getCurrentPractice } from './selectors';
import { Group } from '../../models/Group';
import {Template} from '../../interfaces/template';
import { PracticeConfigInterface } from '../../interfaces/practice-config.interface';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '../../services/environment.service';
import { of } from 'rxjs';
import { getEmbedSettings, isEmbedMode } from '../../state/selectors';

@Injectable()
export class PracticeEffects {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private store: Store<AppState>,
    private cookieService: CookieService,
    private http: HttpClient,
    private environmentService: EnvironmentService,
  ) {}

  getStaff$ = createEffect(() => this.actions$.pipe(
    ofType(PracticeActions.GetPracticeStaff),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.userService.getPracticeStaff(practice!.id)
      .pipe(
        map((result: User[]) => {
          return PracticeActions.GetPracticeStaffSuccess({users: result});
        })
      )
    )
  ));

  getGroups$ = createEffect(() => this.actions$.pipe(
    ofType(PracticeActions.GetPracticeGroups),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.userService.getPracticeGroups(practice!.id)
      .pipe(
        map((result: Group[]) => {
          return PracticeActions.GetPracticeGroupsSuccess({groups: result});
        })
      )
    )
  ));

  getTemplates$ = createEffect(() => this.actions$.pipe(
    ofType(PracticeActions.GetPracticeTemplates),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.userService.getPracticeTemplates(practice!.id)
      .pipe(
        map((result: Template[]) => {
          return PracticeActions.GetPracticeTemplatesSuccess({templates: result});
        })
      )
    )
  ));

  getUserPractices$ = createEffect(() => this.actions$.pipe(
    ofType(PracticeActions.GetUserPractices),
    mergeMap(() => this.userService.getUserPractices()
      .pipe(
        map((result: Practice[]) => {
          return PracticeActions.GetUserPracticesSuccess({practices: result});
        })
      )
    )
  ));

  getUserPracticesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PracticeActions.GetUserPracticesSuccess),
      withLatestFrom(
        this.store.pipe(select(isEmbedMode)),
        this.store.pipe(select(getEmbedSettings))
      ),
      switchMap(([action, embedMode, embedSettings]) => {
        const practices = action.practices;
        
        if (embedMode && embedSettings?.departmentId && embedSettings?.cloudId) {
          return this.http.get<{ corePracticeId: number, messagingPracticeId: number, region: string }>(
            `${this.environmentService.get('authUrl')}/embed-auth/practice-lookup`,
            {
              params: {
                cloudId: embedSettings.cloudId,
                departmentId: embedSettings.departmentId
              },
              withCredentials: true,
              headers: {
                Authorization: `Bearer ${this.cookieService.get('access_token')}`
              }
            }
          ).pipe(
            map(response => {
              const matched = practices.find(p => String(p.id) === String(response.messagingPracticeId));
              return matched
                ? PracticeActions.SetSelectedPractice({ practice: matched })
                : Noop(); // fallback if no match found
            }),
            catchError(error => {
              console.error('Practice lookup failed:', error);
              return of(Noop());
            })
          );
        }
  
        // Not embed mode or no departmentId — fallback to cookie or first practice
        let selected: Practice | undefined;
        const cookiePractice = this.cookieService.get('messaging-selected-practice');
        if (cookiePractice) {
          selected = practices.find(p => String(p.id) === cookiePractice);
        }
        if (!selected && practices.length > 0) {
          selected = practices[0];
        }
  
        return of(
          selected
            ? PracticeActions.SetSelectedPractice({ practice: selected })
            : Noop()
        );
      })
    )
  );

  setCurrentPractice$ = createEffect(() => this.actions$.pipe(
    ofType(PracticeActions.SetSelectedPractice),
    tap((action) => {
      if (action.practice.id) {
        this.store.dispatch(PracticeActions.GetPracticeStaff());
        this.store.dispatch(PracticeActions.GetPracticeConfig());
        const expires = addYears(new Date(), 10);
        this.cookieService.set('messaging-selected-practice', action.practice.id, expires, '/');
      }
    })
  ), {dispatch: false});

  getConfig$ = createEffect(() => this.actions$.pipe(
    ofType(PracticeActions.GetPracticeConfig),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.userService.getPracticeConfig(practice!.id)
      .pipe(
        map((result: PracticeConfigInterface) => {
          return PracticeActions.GetPracticeConfigSuccess({config: result});
        })
      )
    )
  ));
}
